import { useState } from 'react';

import { ETypeSector, PartnerDetails, partnerTypeNames, PartnerTypes } from 'apis/PartnerApi';
import { GMetrixComboBoxList } from 'components/GMetrixComboBoxList/GMetrixComboBoxList';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { usePartnerCreateEdit } from 'pages/partner_create_edit/context';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Country } from 'apis/CountryApi';
import { StateApi } from 'apis/StateApi';

interface PartnerDetailsSectionProps {
    formDetails: PartnerDetails;
    licenseCount?: number;
    onSave: (details?: PartnerDetails, countries?: Country[]) => Promise<boolean>;
    operatingCountries?: Country[];
    readOnly: boolean;
    saving: boolean;
    setFormDetails: (details: Partial<PartnerDetails>) => void;
    setOperatingCountries?: (countries: Country[]) => void;
    userCount?: number;
    validation: {
        contactEmailIsValid: boolean;
        homeCountryIsValid: boolean;
        languageIsValid: boolean;
        organizationIsValid: boolean;
        sectorIsValid: boolean;
        stateIsValid: boolean;
    };
}

export const PartnerDetailsSection = ({ formDetails, licenseCount, onSave, operatingCountries, readOnly, saving, setFormDetails, setOperatingCountries, userCount, validation }: PartnerDetailsSectionProps) => {

    const { formatMessage } = useIntl();
    const { countries, languages, partnerType, setStates, states } = usePartnerCreateEdit();
    const { catchWithToast } = useNotification();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editDetails, setEditDetails] = useState<PartnerDetails | null>(null);
    const [editOperatingCountries, setEditOperatingCountries] = useState<Country[] | null>(null);

    const showOperatingCountries = partnerType !== PartnerTypes.TestingCenter && operatingCountries !== undefined && setOperatingCountries !== undefined;

    // In modal mode, use local edit state; otherwise use parent state
    const modalDetails = editDetails ?? formDetails;
    const modalOperatingCountries = editOperatingCountries ?? operatingCountries ?? [];

    const availableCountries = showOperatingCountries
        ? countries.filter(c => !modalOperatingCountries.find(oc => oc.Id === c.Id) && c.Id !== modalDetails.Country.Id)
        : [];

    const sectors = [
        { Id: ETypeSector.K12, Name: formatMessage({ id: 'k12_education.eccc589e', defaultMessage: 'K12 Education' }) },
        { Id: ETypeSector.Government, Name: formatMessage({ id: 'government.28df1366', defaultMessage: 'Government' }) },
        { Id: ETypeSector.Commercial, Name: formatMessage({ id: 'commercial.431dfb1b', defaultMessage: 'Commercial' }) },
        { Id: ETypeSector.HigherEducation, Name: formatMessage({ id: 'higher_education.64009dd3', defaultMessage: 'Higher Education' }) }
    ];

    const handleCountryChange = async (value: Country) => {

        if (editDetails) {

            setEditDetails(prev => prev ? { ...prev, Country: value, State: { Id: 0, Name: '' } } : null);

        } else {

            setFormDetails({ Country: value, State: { Id: 0, Name: '' } });

        }
        try {

            const fetchedStates = await StateApi.GetAll(value.Id);
            setStates(fetchedStates);
            if (fetchedStates.length === 0) {

                if (editDetails) {

                    setEditDetails(prev => prev ? { ...prev, State: { Id: 219, Name: formatMessage({ id: 'not_applicable.a01fc143', defaultMessage: 'Not Applicable' }) || 'Not Applicable' } } : null);

                } else {

                    setFormDetails({ State: { Id: 219, Name: formatMessage({ id: 'not_applicable.a01fc143', defaultMessage: 'Not Applicable' }) || 'Not Applicable' } });

                }

            }

        } catch (err) {

            catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(err);

        }

    };

    const openEditModal = () => {

        setEditDetails({ ...formDetails });
        if (operatingCountries) setEditOperatingCountries([...operatingCountries]);
        setShowEditModal(true);

    };

    const handleCancel = () => {

        setEditDetails(null);
        setEditOperatingCountries(null);
        setShowEditModal(false);

    };

    const handleSave = async () => {

        if (!editDetails) return;
        const success = await onSave(editDetails, editOperatingCountries ?? undefined);
        if (success) {

            // Commit local edits to parent state
            setFormDetails(editDetails);
            if (setOperatingCountries && editOperatingCountries) {

                setOperatingCountries(editOperatingCountries);

            }
            setEditDetails(null);
            setEditOperatingCountries(null);
            setShowEditModal(false);

        }

    };

    const verifyInput = () => modalDetails.Name.length === 0 || modalDetails.ContactEmail.length === 0 || modalDetails.Country.Name.length === 0;

    const sectorName = sectors.find(s => s.Id === formDetails.Sector)?.Name || formatMessage({ id: 'na.022d6995', defaultMessage: 'N/A' });

    const updateEditDetails = (partial: Partial<PartnerDetails>) => {

        if (editDetails) {

            setEditDetails(prev => prev ? { ...prev, ...partial } : null);

        } else {

            setFormDetails(partial);

        }

    };

    const editForm = () => (
        <Form>
            <Row>
                <Col md={showOperatingCountries ? 8 : 12}>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput required label={formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' }) || 'Organization'} onChange={e => updateEditDetails({ Name: e.target.value })} type="text" value={modalDetails.Name} />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput required label={formatMessage({ id: 'corporate_email.a3823c74', defaultMessage: 'Corporate Email', description: 'New' }) || 'Corporate Email'} onChange={e => updateEditDetails({ ContactEmail: e.target.value })} type="text" value={modalDetails.ContactEmail} />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixDropdown
                                required
                                value={{
                                    Id: modalDetails.Sector,
                                    Name: sectors.find(s => s.Id === modalDetails.Sector)?.Name
                                }}
                                dataItemKey="Id"
                                dataItems={sectors}
                                isValid={validation.sectorIsValid}
                                label={formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' }) || 'Sector'}
                                onChange={e => updateEditDetails({ Sector: Array.isArray(e) ? e[0].Id : e.Id })}
                                textField="Name"
                            />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixDropdown
                                required
                                dataItemKey="Id"
                                dataItems={languages}
                                isValid={validation.languageIsValid}
                                label={formatMessage({ id: 'language.bc205d83', defaultMessage: 'Language' }) || 'Language'}
                                onChange={e => updateEditDetails({ Language: Array.isArray(e) ? e[0] : e })}
                                textField="Name"
                                value={modalDetails.Language}
                            />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixDropdown
                                required
                                dataItemKey="Id"
                                dataItems={countries}
                                isValid={validation.homeCountryIsValid}
                                label={formatMessage({ id: 'home_country.829daf61', defaultMessage: 'Home Country' }) || 'Home Country'}
                                onChange={value => handleCountryChange(value as Country)}
                                textField="Name"
                                value={modalDetails.Country}
                            />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={states}
                                disabled={states.length <= 1 || modalDetails.State?.Id === 219}
                                isValid={validation.stateIsValid}
                                label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' }) || 'State/Province'}
                                onChange={e => updateEditDetails({ State: Array.isArray(e) ? e[0] : e })}
                                required={states.length > 1 && modalDetails.State?.Id !== 219}
                                textField="Name"
                                value={modalDetails.State}
                            />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'address_line_1.25dd7c0d', defaultMessage: 'Address Line 1' }) || 'Address Line 1'} onChange={e => updateEditDetails({ Address1: e.target.value })} type="text" value={modalDetails.Address1} />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'address_line_2.74e16278', defaultMessage: 'Address Line 2' }) || 'Address Line 2'} onChange={e => updateEditDetails({ Address2: e.target.value })} type="text" value={modalDetails.Address2} />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'city.e7690187', defaultMessage: 'City' }) || 'City'} onChange={e => updateEditDetails({ City: e.target.value })} type="text" value={modalDetails.City} />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'zippostal_code.9631e317', defaultMessage: 'Zip/Postal Code' }) || 'Zip/Postal Code'} onChange={e => updateEditDetails({ Zip: e.target.value })} type="text" value={modalDetails.Zip} />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'customer_id.16ab92c3', defaultMessage: 'Customer ID', description: 'New' }) || 'Customer ID'} onChange={e => updateEditDetails({ CustomerIds: e.target.value })} type="text" value={modalDetails.CustomerIds} />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'phone.dd52f414', defaultMessage: 'Phone', description: 'New' }) || 'Phone'} onChange={e => updateEditDetails({ Phone: e.target.value })} type="text" value={modalDetails.Phone} />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixInput label={formatMessage({ id: 'icon_url.79edde11', defaultMessage: 'Icon URL' }) || 'Icon URL'} onChange={e => updateEditDetails({ IconURL: e.target.value })} type="text" value={modalDetails.IconURL} />
                        </Form.Group>
                        <Form.Group as={Col} className="mt-2" md={6}>
                            <GMetrixCheckInput
                                checked={modalDetails.Active}
                                label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) || 'Active'}
                                onChange={e => updateEditDetails({ Active: e.target.checked })}
                                type="switch"
                            />
                        </Form.Group>
                    </Row>
                </Col>
                {showOperatingCountries && (
                    <Col md={4}>
                        <div className="mt-2">
                            <GMetrixComboBoxList
                                dataList={availableCountries}
                                displayNameProp="Name"
                                label={formatMessage({ id: 'operating_countries.704a0887', defaultMessage: 'Operating Countries' }) || 'Operating Countries'}
                                selectedListItems={modalOperatingCountries}
                                setDataList={editDetails ? setEditOperatingCountries : (setOperatingCountries as (val: Country[]) => void)}
                            />
                        </div>
                    </Col>
                )}
            </Row>
        </Form>
    );

    if (readOnly) {

        return (
            <Container fluid className="p-0">
                <GMetrixDialog
                    actionButtonClick={handleSave}
                    actionButtonDisabled={verifyInput() || saving}
                    actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' }) || 'Save'}
                    dialogLabel={formatMessage({ id: 'edit_details.429ca491', defaultMessage: 'Edit Details' }) || 'Edit Details'}
                    onCancel={handleCancel}
                    show={showEditModal}
                    size="xl"
                >
                    {editForm()}
                </GMetrixDialog>

                <div className="d-flex justify-content-end mb-3">
                    <GMetrixButton
                        iconClass="fa-pencil fa-solid"
                        onClick={openEditModal}
                        variant="primary"
                    >
                        {<FormattedMessage defaultMessage="Edit" id="edit.00978daf" /> || 'Edit'}
                    </GMetrixButton>
                </div>

                <Row>
                    <Col md={4}>
                        <div>
                            <strong>
                                {<FormattedMessage defaultMessage="Partner Type" id="partner_type.583106ab" /> || 'Partner Type'}
                            </strong>
                            <p className="text-muted mb-0">
                                {partnerTypeNames[formDetails.PartnerType - 1]}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Organization" description="New" id="organization.7cb537a9" /> || 'Organization'}
                            </strong>
                            <p className="text-wrap mb-0">
                                {formDetails.Name || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Sector" description="New" id="sector.343b1273" /> || 'Sector'}
                            </strong>
                            <p className="text-muted mb-0">
                                {sectorName}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Customer ID" description="New" id="customer_id.16ab92c3" /> || 'Customer ID'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.CustomerIds || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Home Country" id="home_country.829daf61" /> || 'Home Country'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Country?.Name || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Address Line 1" id="address_line_1.25dd7c0d" /> || 'Address Line 1'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Address1 || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="City" id="city.e7690187" /> || 'City'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.City || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div>
                            <strong>
                                {<FormattedMessage defaultMessage="Active" id="active.b9616c88" /> || 'Active'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Active
                                    ? <i className="fa-solid fa-check text-success fa-lg"></i>
                                    : <i className="fa-solid fa-xmark text-danger fa-lg"></i>}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Corporate Email" description="New" id="corporate_email.a3823c74" /> || 'Corporate Email'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.ContactEmail || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Language" id="language.bc205d83" /> || 'Language'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Language?.Name || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Phone" description="New" id="phone.dd52f414" /> || 'Phone'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Phone || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="State/Province" id="stateprovince.d2dfa3c8" /> || 'State/Province'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.State?.Name || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Address Line 2" id="address_line_2.74e16278" /> || 'Address Line 2'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Address2 || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Zip/Postal Code" id="zippostal_code.9631e317" /> || 'Zip/Postal Code'}
                            </strong>
                            <p className="text-muted mb-0">
                                {formDetails.Zip || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div>
                            <strong>
                                {<FormattedMessage defaultMessage="Icon URL" id="icon_url.79edde11" /> || 'Icon URL'}
                            </strong>
                            <p className="text-break text-muted mb-0">
                                {formDetails.IconURL || <FormattedMessage defaultMessage="N/A" id="na.022d6995" />}
                            </p>
                        </div>
                        {showOperatingCountries && (
                            <div className="mt-2">
                                <strong>
                                    <FormattedMessage defaultMessage="Operating Countries" id="operating_countries.704a0887" />
                                </strong>
                                <p className="text-muted mb-0">
                                    {operatingCountries.length === 0
                                        ? <FormattedMessage defaultMessage="None" id="none.a51ad9ee" />
                                        : operatingCountries.map(c => c.Name).join(', ')}
                                </p>
                            </div>
                        )}
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Licenses" id="licenses.11b1c36a" /> || 'Licenses'}
                            </strong>
                            <p className="text-muted mb-0">
                                {licenseCount ?? 0}
                            </p>
                        </div>
                        <div className="mt-2">
                            <strong>
                                {<FormattedMessage defaultMessage="Users" id="users.581e673f" /> || 'Users'}
                            </strong>
                            <p className="text-muted mb-0">
                                {userCount ?? 0}
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        );

    }

    // Create mode: inline editable form
    return (
        <Container fluid>
            {editForm()}
        </Container>
    );

};
